<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney } from '../utils/format'
import type { CurrencyOption, PayrollPeriod, PayrollRunSummary } from '../types/finance'

/**
 * `/payroll/runs` — one run per period and currency.
 * Generation pulls approved accruals + adjustments and marks them used/exported.
 */

const { t } = useI18n()
const auth = useAuthStore()
const { listPayrollRuns, listPayrollPeriods, listCurrencies, generatePayrollRun } = useFinanceApi()

const PER_PAGE = 20
const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('payroll_runs.manage'))

const rows = ref<PayrollRunSummary[]>([])
const periods = ref<PayrollPeriod[]>([])
const currencies = ref<CurrencyOption[]>([])
const total = ref(0)
const totalPages = ref(1)
const page = ref(1)
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const generating = ref(false)

const filters = ref({ payroll_period_id: '' as string | number, status: '', currency_code: '' })

const formOpen = ref(false)
const form = reactive({
  payroll_period_id: '' as string | number,
  currency_code: '',
  code: '',
})

const formError = computed(() => {
  if (!form.payroll_period_id) return t('finance.payrollFlow.runs.periodRequired')
  if (!form.currency_code) return t('finance.payrollFlow.runs.currencyRequired')
  return ''
})

const rangeLabel = computed(() =>
  total.value ? t('finance.common.rows', { count: total.value }) : '',
)

function statusLabel(status: string): string {
  const key = `finance.payrollFlow.runStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`
  const label = t(key)
  return label.startsWith('finance.') ? status : label
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listPayrollRuns({
      payroll_period_id: filters.value.payroll_period_id,
      status: filters.value.status || undefined,
      currency_code: filters.value.currency_code.trim() || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    rows.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
    rows.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

async function loadOptions() {
  try {
    const [periodResult, currencyList] = await Promise.all([
      listPayrollPeriods({ page: 1, per_page: 50 }),
      listCurrencies(),
    ])
    periods.value = periodResult.items
    currencies.value = currencyList
  } catch {
    periods.value = []
    currencies.value = []
  }
}

function openForm() {
  formOpen.value = true
  actionError.value = ''
  actionMessage.value = ''
  form.code = ''
  if (!form.payroll_period_id && periods.value.length) {
    form.payroll_period_id =
      periods.value.find((period) => period.status === 'open')?.id ?? periods.value[0].id
  }
  if (!form.currency_code && currencies.value.length) {
    form.currency_code =
      currencies.value.find((currency) => currency.is_base)?.code ?? currencies.value[0].code
  }
}

async function generate() {
  actionError.value = ''
  actionMessage.value = ''
  if (formError.value) {
    actionError.value = formError.value
    return
  }
  generating.value = true
  try {
    const run = await generatePayrollRun({
      payroll_period_id: form.payroll_period_id,
      currency_code: form.currency_code,
      code: form.code.trim() || undefined,
    })
    actionMessage.value = run
      ? t('finance.payrollFlow.runs.generated', { code: run.code })
      : t('finance.payrollFlow.runs.generatedPlain')
    formOpen.value = false
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    generating.value = false
  }
}

watch(
  () => [filters.value.payroll_period_id, filters.value.status, filters.value.currency_code],
  () => {
    page.value = 1
    void load()
  },
)

onMounted(() => {
  void loadOptions()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.runs.eyebrow') }}</p>
      <h2>{{ t('finance.payrollFlow.runs.title') }}</h2>
      <p>{{ t('finance.payrollFlow.runs.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="filters.payroll_period_id" class="filter-select">
      <option value="">{{ t('finance.payrollFlow.allPeriods') }}</option>
      <option v-for="period in periods" :key="period.id" :value="period.id">
        {{ period.code }} · {{ period.name }}
      </option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option value="draft">{{ statusLabel('draft') }}</option>
      <option value="approved">{{ statusLabel('approved') }}</option>
      <option value="posted">{{ statusLabel('posted') }}</option>
      <option value="paid">{{ statusLabel('paid') }}</option>
      <option value="reversed">{{ statusLabel('reversed') }}</option>
    </select>
    <input v-model="filters.currency_code" class="filter-select" type="text" placeholder="UZS">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.refresh') }}
    </button>
    <span class="spacer" />
    <button v-if="canManage" class="btn-primary" type="button" @click="openForm">
      {{ t('finance.payrollFlow.runs.generate') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <section v-if="formOpen" class="form-card">
    <h3>
      {{ t('finance.payrollFlow.runs.generate') }}
      <small>{{ t('finance.payrollFlow.runs.generateHint') }}</small>
    </h3>
    <div class="form-grid tight">
      <div class="field">
        <label for="run-period">{{ t('finance.payrollFlow.runs.period') }}</label>
        <select id="run-period" v-model="form.payroll_period_id">
          <option value="">{{ t('finance.payrollFlow.selectPeriod') }}</option>
          <option v-for="period in periods" :key="period.id" :value="period.id">
            {{ period.code }} · {{ period.name }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="run-currency">{{ t('finance.common.currency') }}</label>
        <select id="run-currency" v-model="form.currency_code">
          <option value=""></option>
          <option v-for="currency in currencies" :key="currency.code" :value="currency.code">
            {{ currency.code }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="run-code">{{ t('finance.payrollFlow.runs.code') }}</label>
        <input id="run-code" v-model="form.code" type="text" placeholder="RUN-2026-09-UZS">
        <small>{{ t('finance.payrollFlow.runs.codeHint') }}</small>
      </div>
    </div>
    <p v-if="formError" class="error-inline">{{ formError }}</p>
    <div class="modal-actions">
      <button class="btn-secondary" type="button" @click="formOpen = false">
        {{ t('finance.common.cancel') }}
      </button>
      <button class="btn-primary" type="button" :disabled="generating || Boolean(formError)" @click="generate">
        {{ generating ? t('finance.common.saving') : t('finance.payrollFlow.runs.generate') }}
      </button>
    </div>
  </section>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <p v-else-if="!rows.length" class="state-message">{{ t('finance.payrollFlow.runs.empty') }}</p>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payrollFlow.runs.code') }}</th>
            <th>{{ t('finance.payrollFlow.runs.period') }}</th>
            <th>{{ t('finance.payrollFlow.runs.range') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.gross') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.deductions') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.net') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="run in rows" :key="run.id">
            <td class="cell-code">{{ run.code }}</td>
            <td>{{ run.payroll_period_code || '—' }}</td>
            <td class="cell-date">
              {{ run.date_from ? formatDisplayDate(run.date_from) : '—' }} –
              {{ run.date_to ? formatDisplayDate(run.date_to) : '—' }}
            </td>
            <td class="cell-num">{{ formatMoney(run.gross_total, run.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(run.deduction_total, run.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(run.net_total, run.currency_code) }}</td>
            <td>
              <span class="status-badge" :class="`status-${run.status}`">
                {{ statusLabel(run.status) }}
              </span>
            </td>
            <td class="cell-actions">
              <RouterLink class="link-action" :to="`/payroll/runs/${run.id}`">
                {{ t('finance.common.open') }}
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button class="btn-secondary" type="button" :disabled="page <= 1" @click="page = page - 1; load()">
        {{ t('finance.common.previous') }}
      </button>
      <button
        class="btn-secondary"
        type="button"
        :disabled="page >= totalPages"
        @click="page = page + 1; load()"
      >
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>
</template>

<style scoped>
.status-draft {
  border-color: #cfd8d1;
  color: #5f6b62;
  background: #f6f8f6;
}

.status-approved,
.status-paid {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-posted {
  border-color: #7fa6d9;
  color: #274b7a;
  background: #f0f5fb;
}

.status-reversed {
  border-color: #e0b0a6;
  color: #a13c2c;
  background: #fdf3f1;
}
</style>
